import { useTranslation } from "react-i18next";
// WhatsNewModal — shows once per APP_VERSION bump so users (and you) get
// instant confirmation a fresh build is live. Solves the "Preview vs Prod"
// confusion: when the modal pops up, you KNOW the new deploy succeeded.
//
// Storage:
//   localStorage["wing.lastSeenVersion"] = APP_VERSION  (after dismiss)
//
// Behaviour:
//   - First-ever visit: silent (no modal, just stamps current version).
//   - Subsequent visits where stored version != APP_VERSION: shows entries
//     newer than the stored version.
//   - Auth-aware: only renders for logged-in users (so it doesn't pop on the
//     login/welcome screens).

import { useEffect, useState } from "react";
import { Sparkles, X } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { APP_VERSION, CHANGELOG } from "@/changelog";
import { useBodyScrollLock } from "@/lib/useBodyScrollLock";
const STORAGE_KEY = "wing.lastSeenVersion";

// Compare semver-ish strings ("1.4.0" > "1.3.0"). Falls back to
// lexicographic comparison if a part isn't numeric.
function isNewer(a, b) {
  const pa = String(a).split(".").map(n => parseInt(n, 10) || 0);
  const pb = String(b).split(".").map(n => parseInt(n, 10) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const da = pa[i] ?? 0;
    const db = pb[i] ?? 0;
    if (da !== db) return da > db;
  }
  return false;
}
export default function WhatsNewModal() {
  const {
    t
  } = useTranslation();
  const {
    user
  } = useAuth();
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState([]);
  useBodyScrollLock(open);
  useEffect(() => {
    if (!user) return;
    let lastSeen;
    try {
      lastSeen = localStorage.getItem(STORAGE_KEY);
    } catch {
      return; // localStorage blocked — silently skip
    }

    // First-ever visit: stamp version, don't show modal.
    if (!lastSeen) {
      try {
        localStorage.setItem(STORAGE_KEY, APP_VERSION);
      } catch {/* noop */}
      return;
    }

    // Already on the latest version — nothing to show.
    if (lastSeen === APP_VERSION) return;

    // Show every entry newer than the stored version.
    const fresh = CHANGELOG.filter(e => isNewer(e.version, lastSeen));
    if (fresh.length === 0) {
      // No matching entries (e.g. version downgrade); just stamp.
      try {
        localStorage.setItem(STORAGE_KEY, APP_VERSION);
      } catch {/* noop */}
      return;
    }
    setEntries(fresh);
    setOpen(true);
  }, [user]);
  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, APP_VERSION);
    } catch {/* noop */}
    setOpen(false);
  };
  if (!open || entries.length === 0) return null;
  return <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm animate-fadein" data-testid="whats-new-overlay" onClick={dismiss}>
            <div className="relative w-full sm:max-w-md mx-2 sm:mx-4 mb-2 sm:mb-0 rounded-3xl bg-card border border-border/60 shadow-2xl overflow-hidden" style={{
      paddingBottom: "env(safe-area-inset-bottom)"
    }} onClick={e => e.stopPropagation()} data-testid="whats-new-modal">
                {/* Header */}
                <div className="px-5 pt-5 pb-3 flex items-start gap-3 border-b border-border/40">
                    <div className="h-9 w-9 rounded-full bg-[#A855F7]/15 flex items-center justify-center shrink-0">
                        <Sparkles size={16} className="text-[#A855F7]" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            {t("whatsNewModal.wat_is_er_nieuw")}
                        </p>
                        <p className="font-display text-lg font-semibold tracking-tight leading-tight mt-0.5">
                            {t("whatsNewModal.versie")} {APP_VERSION} {t("whatsNewModal.is_live")}
                        </p>
                    </div>
                    <button type="button" onClick={dismiss} aria-label={t("whatsNewModal.sluiten")} data-testid="whats-new-close" className="h-8 w-8 rounded-full hover:bg-secondary flex items-center justify-center text-muted-foreground transition-colors">
                        <X size={16} />
                    </button>
                </div>

                {/* Body — scrollable list of entries */}
                <div className="px-5 py-4 space-y-5 max-h-[60dvh] overflow-y-auto" data-testid="whats-new-body">
                    {entries.map(e => <div key={e.version} data-testid={`whats-new-entry-${e.version}`}>
                            <div className="flex items-baseline gap-2 mb-2">
                                <h3 className="font-display text-base font-semibold tracking-tight">
                                    {e.title}
                                </h3>
                                <span className="text-[10px] font-medium text-muted-foreground tabular-nums">
                                    v{e.version} · {e.date}
                                </span>
                            </div>
                            <ul className="space-y-1.5">
                                {e.items.map((item, i) => <li key={i} className="flex gap-2 text-sm leading-snug text-foreground/90">
                                        <span className="text-[#A855F7] mt-0.5 shrink-0">•</span>
                                        <span>{item}</span>
                                    </li>)}
                            </ul>
                        </div>)}
                </div>

                {/* Footer */}
                <div className="px-5 py-4 border-t border-border/40">
                    <button type="button" onClick={dismiss} data-testid="whats-new-dismiss" className="w-full rounded-full bg-[#A855F7] text-white py-3 font-semibold
                                   hover:bg-[#9333EA] transition-colors active:scale-[0.98]">
                        {t("whatsNewModal.got_it")}
                    </button>
                </div>
            </div>
        </div>;
}